import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"
import { STORE_CODES } from "../stores"
import { masterCategorySchema } from "./primitives"

// ── Product index (dashboard catalog mirror) ───────────────────────────────

export const PRODUCTS_LIST_DEFAULT_LIMIT = 24
export const PRODUCTS_LIST_MAX_LIMIT = 60
export const PRODUCTS_LIST_MAX_QUERY_LENGTH = 100

/** Tri-state product-index flag filter. A boolean could only say "keep the
 *  matching configs" or "say nothing", which left the off state ambiguous —
 *  "listed only" and "listed + delisted" looked identical. Each axis now names
 *  its three answers: `any` leaves it unfiltered, `yes` keeps only matching
 *  configs, `no` keeps only non-matching ones. */
export const productFlagFilterSchema = z.enum(["any", "yes", "no"])
export type ProductFlagFilter = z.infer<typeof productFlagFilterSchema>

export const PRODUCT_CATALOG_MARKETPLACE_STATE = {
  all: "all",
  ok: "ok",
  nok: "nok",
  pending: "pending",
} as const

export const productCatalogMarketplaceStateSchema = z.enum([
  PRODUCT_CATALOG_MARKETPLACE_STATE.all,
  PRODUCT_CATALOG_MARKETPLACE_STATE.ok,
  PRODUCT_CATALOG_MARKETPLACE_STATE.nok,
  PRODUCT_CATALOG_MARKETPLACE_STATE.pending,
])
export type ProductCatalogMarketplaceState = z.infer<typeof productCatalogMarketplaceStateSchema>

export const PRODUCT_CATALOG_OFFER_STATE = {
  all: "all",
  active: "active",
  partial: "partial",
  inactive: "inactive",
  missing: "missing",
  unknown: "unknown",
} as const

export const productCatalogOfferStateSchema = z.enum([
  PRODUCT_CATALOG_OFFER_STATE.all,
  PRODUCT_CATALOG_OFFER_STATE.active,
  PRODUCT_CATALOG_OFFER_STATE.partial,
  PRODUCT_CATALOG_OFFER_STATE.inactive,
  PRODUCT_CATALOG_OFFER_STATE.missing,
])
export type ProductCatalogOfferState = z.infer<typeof productCatalogOfferStateSchema>

const productsFilterShape = {
  store: z.enum(STORE_CODES).optional(),
  category: masterCategorySchema.optional(),
  q: z.string().trim().min(1).max(PRODUCTS_LIST_MAX_QUERY_LENGTH).optional(),
  // Flag filters. `ownBrand` = the store's own (private-label) brands, resolved
  // from the store config's `eligibility.allowedBrands`. `delisted` /
  // `outOfStock` are `yes` only when EVERY enabled variant of the config
  // matches, so `no` means "at least one variant is still listed / sellable".
  //
  // `outOfStock` is a SELLABILITY axis, not an inventory one: the flat-product
  // builder (`integrations/magento/sql/flat-product.ts` — NOT sync) folds
  // delisted + long-term-unavailable into `stock_status` before it is stored, so
  // the delisted result set is a subset of the out-of-stock one.
  // `inventoryInStock` is the unfolded Magento signal and the only axis that can
  // answer "does this product still have stock?": `yes` means at least one enabled
  // variant is KNOWN to have stock, `no` that none is known to. The aggregate
  // behind it folds NULL to false, so genuinely-empty, "Magento told us nothing"
  // and "not synced since this column existed" all land in `no`. `delisted=yes` +
  // `inventoryInStock=yes` is a delisted product still selling through.
  //
  // `live` is the render pipeline's own `product_not_live` predicate
  // (`modules/products/live.ts`, which owns it; `product-visuals`' target check
  // is a caller) turned into a filter axis, not a
  // fourth definition of "is this product alive": `enabled AND NOT localBan AND
  // NOT longTermUnavailable AND (NOT isDelisted OR inventoryInStock = true)`,
  // `yes` when ANY enabled variant passes it. The catalog browser exposes this
  // axis as the single boolean that replaced the Listing and Stock controls.
  //
  // The API defaults every axis to `any`; the dashboard sends its own opinionated
  // defaults explicitly.
  ownBrand: productFlagFilterSchema.default("any"),
  delisted: productFlagFilterSchema.default("any"),
  outOfStock: productFlagFilterSchema.default("any"),
  inventoryInStock: productFlagFilterSchema.default("any"),
  live: productFlagFilterSchema.default("any"),
}

/** Shareable dashboard filters for the bounded catalog-mirror product index. */
export const productsListQuerySchema = z
  .object({
    ...productsFilterShape,
    marketplace: marketplaceIdSchema.optional(),
    marketplaceState: productCatalogMarketplaceStateSchema.optional(),
    offerState: productCatalogOfferStateSchema.optional(),
    offerSnapshotVersion: z.string().min(1).max(256).optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(PRODUCTS_LIST_MAX_LIMIT)
      .default(PRODUCTS_LIST_DEFAULT_LIMIT),
    offset: z.coerce.number().int().nonnegative().default(0),
  })
  .superRefine((query, context) => {
    if (query.marketplace !== undefined && query.store !== undefined) {
      context.addIssue({
        code: "custom",
        path: ["store"],
        message: "store cannot be supplied with marketplace; the marketplace config owns store",
      })
    }
    if (query.marketplace === undefined && query.marketplaceState !== undefined) {
      context.addIssue({
        code: "custom",
        path: ["marketplaceState"],
        message: "marketplaceState requires marketplace",
      })
    }
    if (query.marketplace === undefined && query.offerState !== undefined) {
      context.addIssue({
        code: "custom",
        path: ["offerState"],
        message: "offerState requires marketplace",
      })
    }
    if (query.marketplace === undefined && query.offerSnapshotVersion !== undefined) {
      context.addIssue({
        code: "custom",
        path: ["offerSnapshotVersion"],
        message: "offerSnapshotVersion requires marketplace",
      })
    }
    if (query.offset === 0 && query.offerSnapshotVersion !== undefined) {
      context.addIssue({
        code: "custom",
        path: ["offerSnapshotVersion"],
        message: "offerSnapshotVersion is only accepted after the first page",
      })
    }
    if (
      query.marketplace !== undefined &&
      query.offset > 0 &&
      query.offerSnapshotVersion === undefined
    ) {
      context.addIssue({
        code: "custom",
        path: ["offerSnapshotVersion"],
        message: "offerSnapshotVersion is required after the first marketplace page",
      })
    }
  })
export type ProductsListQuery = z.infer<typeof productsListQuerySchema>

/** Strict nutrition coverage belongs to product detail Visuals. */
export const productNutritionCoverageSchema = z.object({
  variants: z.number().int().positive(),
  withValues: z.number().int().nonnegative(),
  rendered: z.number().int().nonnegative(),
})
export type ProductNutritionCoverage = z.infer<typeof productNutritionCoverageSchema>

const selectedMarketplaceStateSchema = z.enum([
  PRODUCT_CATALOG_MARKETPLACE_STATE.ok,
  PRODUCT_CATALOG_MARKETPLACE_STATE.nok,
  PRODUCT_CATALOG_MARKETPLACE_STATE.pending,
])

export const productsListItemSchema = z.object({
  configSku: z.string(),
  name: z.string().nullable(),
  mainImage: z.string().nullable(),
  // The mirror column is intentionally plain text. Unknown historical values
  // remain visible in "All" rather than making the whole response invalid.
  masterCategory: z.string().nullable(),
  isCatalogLive: z.boolean(),
  selectedMarketplace: z
    .object({
      state: selectedMarketplaceStateSchema,
      isStale: z.boolean(),
      isSetAside: z.boolean(),
      isDelisted: z.boolean(),
    })
    .nullable(),
  offerActivity: z
    .object({
      state: z.union([
        productCatalogOfferStateSchema.exclude([PRODUCT_CATALOG_OFFER_STATE.all]),
        z.literal(PRODUCT_CATALOG_OFFER_STATE.unknown),
      ]),
      matchedOfferCount: z.number().int().nonnegative(),
      enabledVariantCount: z.number().int().positive(),
      activeOfferCount: z.number().int().nonnegative(),
      unknownOfferCount: z.number().int().nonnegative().optional(),
    })
    .nullable()
    .optional(),
})
export type ProductsListItem = z.infer<typeof productsListItemSchema>

export const productsListResponseSchema = z.object({
  store: z.enum(STORE_CODES),
  products: z.array(productsListItemSchema),
  pagination: z.object({
    offset: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    hasMore: z.boolean(),
  }),
  marketplaceStateCounts: z
    .object({
      all: z.number().int().nonnegative(),
      ok: z.number().int().nonnegative(),
      nok: z.number().int().nonnegative(),
      pending: z.number().int().nonnegative(),
    })
    .optional(),
  offerStateCounts: z
    .object({
      all: z.number().int().nonnegative(),
      active: z.number().int().nonnegative(),
      partial: z.number().int().nonnegative(),
      inactive: z.number().int().nonnegative(),
      missing: z.number().int().nonnegative(),
    })
    .optional(),
  offerSnapshot: z
    .object({
      capturedAt: z.string(),
      freshness: z.enum(["fresh", "stale"]),
      activityAvailable: z.boolean(),
    })
    .nullable()
    .optional(),
  offerSnapshotVersion: z.string().min(1).max(256).optional(),
})
export type ProductsListResponse = z.infer<typeof productsListResponseSchema>
